import { gql, useQuery } from '@apollo/client';
import * as React from 'react';

import { logError } from '@/imports/logging/ClientLogger';
import { MobileSupportingContent } from '@/imports/ui/components/MobileSupportingContent';
import { VideoSupportingContent } from '@/imports/ui/components/VideoSupportingContent';

import { useAttendeeData_EventAttendee } from './graphql-types/useAttendeeData_EventAttendee';
import {
  useAttendeeData_currentAttendeesQuery,
  useAttendeeData_currentAttendeesQueryVariables,
} from './graphql-types/useAttendeeData_currentAttendeesQuery';

const ATTENDEE_DATA_FRAGMENT = gql`
  fragment useAttendeeData_EventAttendee on EventAttendee {
    _id
    ...VideoSupportingContent_EventAttendeeFragment
    ...MobileSupportingContent_EventAttendeeFragment
  }
  ${VideoSupportingContent.eventAttendeeFragment}
  ${MobileSupportingContent.eventAttendeeFragment}
`;

const GET_CURRENT_ATTENDEES_AND_CURRENT_USER = gql`
  query useAttendeeData_currentAttendeesQuery(
    $input: ActiveEventAttendeesInput!
  ) {
    activeEventAttendees(input: $input) {
      activeEventAttendees {
        _id
        ...useAttendeeData_EventAttendee
      }
    }
    currentUser {
      _id
    }
  }
  ${ATTENDEE_DATA_FRAGMENT}
`;

const getConnectionDataFromConnection = (
  connectionData: string
): Omit<useAttendeeData_EventAttendee, '__typename'> | undefined => {
  try {
    const parsedConnectionData = JSON.parse(connectionData);
    const {
      id,
      displayName,
      proxyProfilePictureUrl,
      jobTitle,
      company,
      nameTitle,
    } = parsedConnectionData.attendee;
    if (!id || !displayName) {
      logError(
        new Error(
          `ConnectionParseError: id: ${id} and displayName: ${displayName} must be defined`
        )
      );
      return undefined;
    }

    return {
      _id: id,
      displayName,
      proxyProfilePictureUrl: proxyProfilePictureUrl || null,
      jobTitle: jobTitle || null,
      company: company || null,
      nameTitle: nameTitle || null,
      sip: parsedConnectionData.sip || false,
      authenticated: parsedConnectionData.authenticated || false,
    };
  } catch (err) {
    logError(
      new Error(
        `ConnectionParseError: ${err.message}. Connection data: ${connectionData}`
      )
    );
  }
  return undefined;
};

/**
 * This hook is responsible for two things. Ideally this would be two hooks but we are
 * combining them because we only want one query to poll the server.
 *
 * First, it stitches together participant data provided by tokbox and attendee data which
 * it gets from our graphql server.
 *
 * Second, it refetches the id of the current user. If the user gets logged out because
 * they get deactivated, we want to update the apollo cache and have the code in restrictedRoute
 * kick the user out of the event. We're relying on the user's auth status because tokbox doesn't
 * give us a way to kick a user out from the server.
 */
export const useAttendeeAndLoggedInUserData = (input: {
  vonageSessionId?: string;
  participantConnectionData: string[];
}): {
  attendeeIdToInformation: Map<
    string,
    Omit<useAttendeeData_EventAttendee, '__typename'>
  >;
  numAttendees: number;
} => {
  const { vonageSessionId, participantConnectionData } = input;

  const [attendeeIdToConnectionData, setAttendeeIdToConnectionData] =
    React.useState<
      Map<string, Omit<useAttendeeData_EventAttendee, '__typename'>>
    >(new Map());

  const { data, loading, error } = useQuery<
    useAttendeeData_currentAttendeesQuery,
    useAttendeeData_currentAttendeesQueryVariables
  >(GET_CURRENT_ATTENDEES_AND_CURRENT_USER, {
    skip: !vonageSessionId,
    variables: { input: { sessionId: vonageSessionId! } },
    pollInterval: 10000,
    fetchPolicy: 'network-only', // Have to do this to ensure polling works
  });

  const queryData = React.useMemo(
    () =>
      loading || error || !data
        ? []
        : data.activeEventAttendees.activeEventAttendees,
    [data, error, loading]
  );

  const participantData = React.useMemo(
    () =>
      participantConnectionData
        .map((dataString) => getConnectionDataFromConnection(dataString))
        .filter(
          (data): data is Omit<useAttendeeData_EventAttendee, '__typename'> =>
            !!data
        ),
    [participantConnectionData]
  );

  React.useEffect(() => {
    const queryDataForMap: [
      string,
      Omit<useAttendeeData_EventAttendee, '__typename'>
    ][] = queryData.map((attendeeData) => [attendeeData._id, attendeeData]);
    const participantDataForMap: [
      string,
      Omit<useAttendeeData_EventAttendee, '__typename'>
    ][] = participantData.map((attendeeData) => [
      attendeeData._id,
      attendeeData,
    ]);
    setAttendeeIdToConnectionData(
      new Map([...participantDataForMap, ...queryDataForMap])
    );
  }, [participantData, queryData]);

  const numAttendees = attendeeIdToConnectionData.size;

  return { attendeeIdToInformation: attendeeIdToConnectionData, numAttendees };
};
